import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PiList, PiMagnifyingGlass } from 'react-icons/pi'
import { NotificationButton } from 'components/Notification/NotificationButton'
import { useAuth } from 'context/authentication/useAuth'
import { useLeftNavStore } from 'components/Layout/LeftNav'
import { useLayoutConfig } from 'context/layout'
import { Z_INDEX } from 'styles/z-index'
import { Breadcrumbs } from './Breadcrumbs'
import { useBreadcrumbs } from './useBreadcrumbs'
import type { BreadcrumbItem } from './types'
import logoWhite from 'assets/Name - White Transparent.svg'
import apiService from 'services/api'
import { API_HOST } from 'utils/http-client'

interface HeaderProps {
  onSearchClick?: () => void
}

export const Header: React.FC<HeaderProps> = ({
  onSearchClick,
}): React.ReactElement => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isCollapsed, toggleCollapsed } = useLeftNavStore()
  const { config } = useLayoutConfig()
  const api = apiService()

  // Fetch company details to get the logo
  const { data: companyData } = api.useGetCompanyDetails()

  // Get the primary company logo if available
  const primaryLogoUrl = useMemo(() => {
    if (!companyData?.logos) return null

    const primaryLogo = companyData.logos.find((logo) => logo.is_primary)
    if (!primaryLogo?.logo_url) return null

    try {
      return new URL(primaryLogo.logo_url, API_HOST).href
    } catch {
      return null
    }
  }, [companyData])

  // Get breadcrumb items from the centralized hook
  const { items: baseBreadcrumbItems } = useBreadcrumbs()

  // Combine base breadcrumbs with any entity-specific breadcrumbs from layout config
  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    if (config.entityBreadcrumbs?.length) {
      return [...baseBreadcrumbItems, ...config.entityBreadcrumbs]
    }
    return baseBreadcrumbItems
  }, [baseBreadcrumbItems, config.entityBreadcrumbs])

  const handleMenuToggle = useCallback(() => {
    toggleCollapsed()
  }, [toggleCollapsed])

  const handleSearchClick = useCallback(() => {
    if (onSearchClick) {
      onSearchClick()
    }
  }, [onSearchClick])

  const handleAvatarClick = useCallback(() => {
    navigate('/settings/profile')
  }, [navigate])

  // Get user initials for avatar fallback
  const userInitials = useMemo(() => {
    if (!user?.first_name && !user?.last_name) return '?'
    const first = user.first_name?.charAt(0) || ''
    const last = user.last_name?.charAt(0) || ''
    return (first + last).toUpperCase()
  }, [user])

  return (
    <header
      className="flex items-center h-[var(--header-height)] min-h-[var(--header-height)] bg-[var(--nav-bg-primary)] pr-6 relative"
      style={{ zIndex: Z_INDEX.NAV }}
    >
      {/* Menu Toggle Button */}
      <button
        onClick={handleMenuToggle}
        aria-label="Toggle navigation menu"
        aria-expanded={!isCollapsed}
        className="flex items-center justify-center w-[var(--left-nav-width)] h-full border-none bg-transparent text-[var(--nav-text-muted)] cursor-pointer transition-all duration-150 hover:text-[var(--nav-text-primary)] hover:bg-[var(--nav-bg-hover)]"
      >
        <PiList size={24} />
      </button>

      {/* Logo */}
      <div className="flex items-center justify-center py-[18px]">
        <img src={logoWhite} alt="First Bite" className="h-[45px]" />
      </div>

      {/* Breadcrumbs */}
      <div className="flex-1 flex items-center px-6 min-w-0">
        <Breadcrumbs items={breadcrumbItems} />
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-3">
        {/* Search Bar */}
        <button
          onClick={handleSearchClick}
          aria-label="Search First Bite"
          className="flex items-center gap-2 w-[220px] h-8 px-2 py-1 bg-[#e5ece7] border-none rounded-lg text-[#6e6e6e] text-[12px] cursor-pointer transition-shadow duration-150 shadow-[0px_6px_58px_0px_rgba(196,203,214,0.1)] hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)] max-[1280px]:w-8 max-[1280px]:px-1 max-[1280px]:justify-center"
        >
          <PiMagnifyingGlass
            size={16}
            className="text-[#6e6e6e] flex-shrink-0"
          />
          <span className="flex-1 text-left max-[1280px]:hidden">
            Search First Bite
          </span>
        </button>

        {/* Notification Button */}
        <NotificationButton />

        {/* Avatar Button */}
        <button
          onClick={handleAvatarClick}
          aria-label="User profile"
          className="flex items-center justify-center w-8 h-8 p-0 border-none rounded-lg bg-[var(--nav-bg-hover)] text-[var(--nav-text-primary)] text-[14px] font-medium cursor-pointer overflow-hidden transition-opacity duration-150 hover:opacity-90"
        >
          {primaryLogoUrl ? (
            <img
              src={primaryLogoUrl}
              alt="Company logo"
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
          ) : (
            userInitials
          )}
        </button>
      </div>
    </header>
  )
}
